import { isJwtExpired } from './jwt';

// Tokens live in localStorage only; the auth slice mirrors nothing, so this is the
// single source of truth for "is there a session".
const ACCESS_KEY = 'access_token';
const REFRESH_KEY = 'refresh_token';
const USER_KEY = 'user_data';

export const getAccessToken = () => localStorage.getItem(ACCESS_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);

export const storeTokens = (accessToken, refreshToken) => {
  localStorage.setItem(ACCESS_KEY, accessToken);
  localStorage.setItem(REFRESH_KEY, refreshToken);
};

export const clearStoredSession = () => {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
};

// The access token is good for 7 days and the refresh token for 30, so only a dead
// refresh token actually ends a session.
export const accessTokenExpired = () => isJwtExpired(getAccessToken());
export const sessionIsRecoverable = () => !isJwtExpired(getRefreshToken());

// The API answers a dead JWT with 403, not 401: MongoJWTAuthentication never overrides
// DRF's authenticate_header, so AuthenticationFailed gets downgraded to 403. These are the
// two details that auth class raises.
const AUTH_FAILURE_DETAILS = ['Invalid token', 'User not found'];

// A role-based 403 must not sign anyone out, so a 403 counts as an auth failure only when
// our auth class raised it or the token we sent had already expired.
export const isAuthFailure = (error) => {
  const status = error?.response?.status;
  if (status === 401) return true;
  if (status !== 403) return false;
  if (AUTH_FAILURE_DETAILS.includes(error.response?.data?.detail)) return true;
  return accessTokenExpired();
};

// Set once from the store so the axios interceptor, which lives outside React, can end
// a session without importing the store and creating a cycle.
let onSessionExpired = null;
export const setSessionExpiredHandler = (handler) => {
  onSessionExpired = handler;
};

// Clearing the tokens is also the latch that stops further refresh attempts: with no
// refresh token, queued requests fail fast instead of each retrying the dead session.
export const endSession = () => {
  clearStoredSession();
  if (onSessionExpired) onSessionExpired();
};
